import React, { useContext, useEffect, useRef, useState } from 'react';
import { InputRef } from 'antd';
import { Form, Input, InputNumber, Select, Typography } from 'components';
import { EditableCellProps } from './types';
import { EditableContext } from './context';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

export const EditableCell: React.FC<EditableCellProps> = ({
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  inputType = 'text',
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const contextValue = useContext(EditableContext);

  // Check if this cell should be in edit mode
  const shouldBeEditing =
    editable && record.key === contextValue.editingRow && dataIndex === contextValue.editingColumn;

  // When the cell should be in edit mode
  useEffect(() => {
    if (shouldBeEditing && !editing) {
      setEditing(true);
    }
  }, [shouldBeEditing, editing]);

  // When editing, focus and set value
  useEffect(() => {
    if (editing) {
      if (contextValue.form) {
        // For itemSelector, we need to find the ID that matches the internalItemId
        if (inputType === 'itemSelector' && contextValue.itemSelectorData) {
          // Find the item ID that corresponds to this record's internalItemId
          const itemId = Object.entries(contextValue.itemSelectorData).find(
            ([_, item]) => item.id === record.internalItemId
          )?.[0];

          // If found, set that as the form value, otherwise use the current value
          contextValue.form.setFieldsValue({
            [dataIndex]: itemId || record[dataIndex],
          });
        } else {
          contextValue.form.setFieldsValue({ [dataIndex]: record[dataIndex] });
        }
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [editing, dataIndex, record, contextValue.form, contextValue.itemSelectorData, inputType]);

  const toggleEdit = () => {
    setEditing(!editing);
    // When starting to edit, notify the context to set the current row and column
    if (!editing && contextValue.setEditingRow && contextValue.setEditingColumn) {
      contextValue.setEditingRow(record.key);
      contextValue.setEditingColumn(dataIndex);
    }
  };

  const save = async () => {
    try {
      if (contextValue.form) {
        const values = await contextValue.form.validateFields();
        toggleEdit();
        handleSave({ ...record, ...(values as any) });
      }
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  const handleItemSelect = (itemId: string) => {
    if (contextValue.itemSelectorData && contextValue.itemSelectorData[itemId]) {
      const selectedItemData = contextValue.itemSelectorData[itemId];

      // Update the row with selected item data
      const updatedRow = {
        ...record,
        internalItemId: selectedItemData.id,
        sku: selectedItemData.sku,
        description: selectedItemData.description,
        isSystemItem: true,
        identified: true,
      };

      // Save the updated row
      toggleEdit();
      handleSave(updatedRow);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: dataIndex === 'sku',
            message: `SKU is required`,
          },
        ]}
      >
        {inputType === 'number' ? (
          <InputNumber min={0} ref={inputRef as any} onPressEnter={save} onBlur={save} style={{ width: '100%' }} />
        ) : inputType === 'itemSelector' ? (
          <Select
            style={{ maxWidth: 220 }}
            placeholder="Select an item"
            onChange={handleItemSelect}
            showSearch
            showAdd
            onAdd={() => contextValue.openAddItemModal && contextValue.openAddItemModal()}
            filterOption={(input, option) => {
              const combinedText = Array.isArray(option?.children)
                ? option?.children?.join(' ')
                : option?.children?.toString() || '';
              return combinedText.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {contextValue.itemSelectorData &&
              Object.entries(contextValue.itemSelectorData).map(([id, item]) => (
                <Option key={id} value={id}>
                  <Text style={{ maxWidth: 180 }} ellipsis={{ tooltip: true }}>
                    {item.sku} {item?.description ? ` - ${item?.description}` : ''}
                  </Text>
                </Option>
              ))}
          </Select>
        ) : dataIndex === 'description' ? (
          <TextArea
            ref={inputRef as any}
            onBlur={save}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (!e.shiftKey) {
                  e.preventDefault();
                  save();
                }
                // Shift+Enter will create a new line by default
              }
            }}
          />
        ) : (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24, maxWidth: '100%' }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  return <td {...(restProps as any)}>{childNode}</td>;
};
